import React from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import Stack from '@mui/material/Stack';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockContainer from '../components/GuideMockContainer';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideContractDuplicate: React.FC = () => {
  return (
    <GuidePageLayout
      title="契約申請を複製する"
      description="既存の契約申請の内容をコピーし、メンバーだけを変更して複数の新規申請を一括作成します。"
      breadcrumbs={[{ label: '契約管理', path: '/guide/contract/overview' }]}
    >
      <GuideSection title="前提">
        <GuideTable
          headers={['条件', '内容']}
          rows={[
            ['必要な権限', '申請者（applicantsに含まれるユーザー）'],
            ['対象申請', '新規申請（CREATE）かつ調整中（ADJUSTING）の申請のみ'],
            ['ログイン状態', 'ログイン済みであること'],
            ['事前準備', '複製先のメンバーが要員管理に登録済みであること'],
          ]}
        />
      </GuideSection>

      <GuideSection title="複製ボタンの場所">
        <GuideStepList
          steps={[
            {
              label: '契約申請一覧で対象の申請を選択する',
              description: '新規申請かつ調整中の申請を選択してください。',
            },
            {
              label: '詳細画面のアクションボタンエリアで「複製」ボタンをクリックする',
              description: '複製ボタンはアクションボタン行の一番右端に表示されます。',
              content: (
                <GuideMockContainer label="アクションボタンエリア">
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ pointerEvents: 'none', width: '100%' }}
                  >
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      sx={{
                        bgcolor: 'primary.main',
                        borderRadius: 2,
                        px: 1.5,
                        py: 0.5,
                      }}
                    >
                      <EditIcon sx={{ fontSize: 16, color: '#fff' }} />
                      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#fff' }}>
                        編集
                      </span>
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      sx={{
                        border: 1,
                        borderColor: 'primary.main',
                        borderRadius: 2,
                        px: 1.5,
                        py: 0.5,
                        ml: 'auto !important',
                      }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 16, color: '#1976d2' }} />
                      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1976d2' }}>
                        複製
                      </span>
                    </Stack>
                  </Stack>
                </GuideMockContainer>
              ),
            },
          ]}
        />
      </GuideSection>

      <GuideSection title="手順1: メンバーを選択する">
        <GuideStepList
          steps={[
            { label: '「複製」ボタンをクリックするとメンバー選択ダイアログが表示される' },
            {
              label: '複製先のメンバーを1名以上選択する',
              description:
                '要員名やカナで検索可能です。複数選択できます。選択したメンバーの数だけ新規申請が作成されます。',
            },
            {
              label: '「確認」ボタンをクリックする',
              description: '1名以上選択すると確認ボタンが活性化します。',
            },
          ]}
        />
      </GuideSection>

      <GuideSection title="手順2: プレビューを確認して実行する">
        <GuideStepList
          steps={[
            {
              label: 'プレビュー画面で選択したメンバーの一覧を確認する',
              description:
                '各行にメンバー名とパートナー名（社員の場合は「社員」と表示）が表示されます。ステータス列も表示されます。',
            },
            {
              label: '問題なければ「実行」ボタンをクリックする',
              description: 'キャンセルボタンでメンバー選択画面に戻ることもできます。',
            },
            {
              label: '登録処理が完了するまで待つ',
              description:
                '1件ずつ登録されます。各行の状態がサーキュラー→成功/失敗アイコンに変わります。',
            },
            {
              label:
                '全て成功なら「完了」ボタンを押す。失敗がある場合は「再実行」で失敗行を再処理する',
            },
          ]}
        />
      </GuideSection>

      <GuideSection title="複製される情報">
        <GuideTable
          headers={['項目', '複製', '備考']}
          rows={[
            ['担当者', '○', '複製元と同じ'],
            ['プロジェクト', '○', '複製元と同じ'],
            ['課長', '○', '複製元と同じ'],
            ['メンバー', '×', '選択したメンバーに差し替え'],
            ['都道府県・ロケーション', '○', ''],
            ['契約形態', '○', ''],
            ['契約開始日・終了日', '○', ''],
            ['コスト金額・売上金額', '○', ''],
            ['稼働率', '○', ''],
            ['契約単位・精算幅', '○', ''],
            ['原籍確認シート', '○', ''],
            ['備考', '○', ''],
            ['テンプレート項目', '○', '技術スタック等'],
            ['申請者・申請先', '×', '未設定（調整中として作成）'],
            ['チェックリスト', '×', '初期状態'],
            ['コメント', '×', '新規申請のため空'],
          ]}
        />
      </GuideSection>

      <GuideSection title="ボタンと操作">
        <GuideTable
          headers={['操作', '表示条件', '活性条件', '動作']}
          rows={[
            [
              '複製ボタン',
              '新規申請 + 調整中 + 申請者',
              '常に活性',
              'メンバー選択ダイアログを開く',
            ],
            ['確認ボタン', 'メンバー選択画面', '1名以上選択時', 'プレビュー画面に進む'],
            ['キャンセルボタン', 'プレビュー画面', '常に活性', 'メンバー選択画面に戻る'],
            ['実行ボタン', 'プレビュー画面', '常に活性', '1件ずつ登録APIを実行'],
            ['完了ボタン', '全件登録成功時', '常に活性', 'ダイアログを閉じる'],
            ['再実行ボタン', '登録失敗行がある場合', '常に活性', '失敗行のみ再度登録を試行'],
          ]}
        />
      </GuideSection>

      <GuideSection title="注意事項">
        <Stack spacing={1}>
          <GuideInfoBox>
            複製された申請は全て「調整中」ステータスで作成されます。申請するには各申請を編集画面から申請操作を行ってください。
          </GuideInfoBox>
          <GuideInfoBox>
            同じプロジェクトに複数のメンバーを参画させる場合に便利です。1件作成して条件を設定した後、複製で他のメンバー分を一括作成できます。
          </GuideInfoBox>
          <GuideInfoBox type="warning">
            登録処理中にダイアログを閉じることはできません。処理が完了するまでお待ちください。
          </GuideInfoBox>
          <GuideInfoBox type="warning">
            複製ボタンは「新規申請」かつ「調整中」の申請にのみ表示されます。変更申請・解除申請や、申請中以降のステータスでは利用できません。
          </GuideInfoBox>
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideContractDuplicate;
